import React, { ChangeEvent, useState } from 'react';

import { faMagnifyingGlass, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { withPrefix } from 'gatsby';
import styled, { css } from 'styled-components';

import GenericContainer from 'components/GenericContainer';
import IconButton from 'components/IconButton/IconButton';
import RawButton from 'components/RawButton/RawButton';

const SearchWrapper = styled.div<{ $show?: boolean; $scrollUp: boolean; $alertOpen: boolean; $alertHeight: number }>`
  ${({ theme, $show, $scrollUp, $alertOpen, $alertHeight }) => css`
    height: 0;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    z-index: 100;
    position: fixed;
    top: ${$alertOpen ? `calc(${$alertHeight}rem + ${$scrollUp ? '0rem' : '2.5rem'})` : $scrollUp ? '0' : '2.5rem'};
    transition:
      all 0.5s,
      height 250ms ease-out;
    background-color: ${theme.colors.white};
    padding: 0 5rem;

    @media (max-width: ${theme.breakpoints.lg}) {
      top: ${$alertOpen ? `calc(${$alertHeight}rem)` : '0'};
      padding: 0 1rem 0 1.5rem;
      z-index: 1001;
    }

    ${$show &&
    css`
      height: 3.75rem;
    `}
  `}
`;

const SearchContainer = styled.form`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.875rem;
`;

const SearchInput = styled.input`
  border: none;
  width: 100%;
  font-size: 1rem;
  margin: 0 1rem;

  &[type='search']::-webkit-search-cancel-button,
  &[type='search']::-webkit-search-decoration {
    -webkit-appearance: none;
    appearance: none;
  }

  &:focus {
    border: 0;
    outline: none;
  }
`;

const Search: React.FC<{
  placeholder?: string;
  show?: boolean;
  setShow?: React.Dispatch<React.SetStateAction<boolean>>;
  scrollDirection?: string;
  alertOpen: boolean;
  alertHeight: number;
}> = ({ placeholder, show, setShow, scrollDirection, alertOpen, alertHeight }) => {
  const [searchValue, setSearchValue] = useState('');

  const handleChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  return (
    <SearchWrapper
      $show={show}
      data-testid='searchWrapper'
      $scrollUp={scrollDirection === 'up'}
      $alertOpen={alertOpen}
      $alertHeight={alertHeight / 16}
    >
      <GenericContainer $padding={false}>
        <SearchContainer data-testid='searchForm' action={withPrefix('/search/')}>
          <RawButton data-testid='enterSearchButton' aria-label='Search' type={'submit'}>
            <FontAwesomeIcon aria-hidden='true' icon={faMagnifyingGlass} />
          </RawButton>
          <SearchInput
            type={'search'}
            name={'q'}
            value={searchValue}
            onChange={handleChangeInput}
            data-testid='searchInput'
            placeholder={placeholder ?? ''}
          />
          <IconButton
            type={'reset'}
            icon={faXmark}
            aria-label='Reset'
            data-testid='closeSearch'
            onClick={() => {
              setSearchValue('');
              setShow && setShow(false);
            }}
          />
        </SearchContainer>
      </GenericContainer>
    </SearchWrapper>
  );
};

export default Search;
